import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApplicationContext } from '@particles';
import {
  callGetTablePreferences,
  callSetTablePreferences,
  callResetTablePreferences
} from '@api';

interface UseTableColumnPrefsResult {
  prefs: TablePrefs | null;
  ready: boolean;
  mutate: (next: TablePrefs) => void;
  reset: () => Promise<void>;
}

const EMPTY: UseTableColumnPrefsResult = {
  prefs: null,
  ready: true,
  mutate: () => undefined,
  reset: async () => undefined
};

const DEBOUNCE_MS = 350;

// Per-user persisted column preferences (order / visibility / sort).
// No-op when enabled === false so non-opted Tables don't pay the cost.
export const useTableColumnPrefs = (
  tableName: string,
  enabled: boolean
): UseTableColumnPrefsResult => {
  const {
    state: { user }
  } = useContext(ApplicationContext);
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => ['table-prefs', userId, tableName], [userId, tableName]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await callGetTablePreferences(tableName);
      // fetchAPI double-wraps: response.data is the raw HTTP body, which the
      // server already wraps as { code, data: TablePrefs }. So unwrap twice.
      if (response.code !== 200) return null;
      const inner = response.data as unknown as { code?: number; data?: TablePrefs | null } | null;
      return (inner?.data ?? null) as TablePrefs | null;
    },
    enabled: enabled && Boolean(userId),
    staleTime: 5 * 60 * 1000
  });

  const setMutation = useMutation({
    mutationFn: (body: TablePrefs) => callSetTablePreferences(tableName, body),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TablePrefs | null>(queryKey);
      queryClient.setQueryData<TablePrefs | null>(queryKey, next);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    }
  });

  const debouncedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPrefs = useRef<TablePrefs | null>(null);

  const flush = useCallback(() => {
    if (pendingPrefs.current) {
      setMutation.mutate(pendingPrefs.current);
      pendingPrefs.current = null;
    }
  }, [setMutation]);

  const mutate = useCallback(
    (next: TablePrefs) => {
      // Update cache immediately for UI feedback; debounce the network write.
      queryClient.setQueryData<TablePrefs | null>(queryKey, next);
      pendingPrefs.current = next;
      if (debouncedTimer.current) clearTimeout(debouncedTimer.current);
      debouncedTimer.current = setTimeout(flush, DEBOUNCE_MS);
    },
    [queryClient, queryKey, flush]
  );

  // Flush any pending write when the component using this hook unmounts.
  useEffect(() => {
    return () => {
      if (debouncedTimer.current) {
        clearTimeout(debouncedTimer.current);
        flush();
      }
    };
  }, [flush]);

  const reset = useCallback(async () => {
    if (debouncedTimer.current) {
      clearTimeout(debouncedTimer.current);
      pendingPrefs.current = null;
    }
    await callResetTablePreferences(tableName);
    queryClient.setQueryData<TablePrefs | null>(queryKey, null);
  }, [queryClient, queryKey, tableName]);

  if (!enabled) return EMPTY;

  // ready === true only once we have a userId AND the first fetch has
  // resolved. NOT `!isLoading` — that would be true even when the query is
  // disabled because userId is still loading from ApplicationContext, which
  // would let the Table organism prematurely apply defaults and then ignore
  // the real prefs when they finally arrive.
  return {
    prefs: query.data ?? null,
    ready: Boolean(userId) && query.isFetched,
    mutate,
    reset
  };
};

export default useTableColumnPrefs;
